import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { SurveyTemplateDTO, CreateSurveyTemplateDTO } from '../dtos';
import { useCreateSurveyTemplate, useUpdateSurveyTemplate } from '../hooks/useSurveyTemplates';

interface AddSurveyTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: SurveyTemplateDTO | null;
  onSuccess?: () => void;
}

interface TenantRouteParams extends Record<string, string | undefined> {
  tenantId: string;
}

export const AddSurveyTemplateDialog: React.FC<AddSurveyTemplateDialogProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
}) => {
  const { tenantId } = useParams<TenantRouteParams>();
  const createMutation = useCreateSurveyTemplate(tenantId!);
  const updateMutation = useUpdateSurveyTemplate(tenantId!);

  const isEditing = !!editingItem;
  const mutation = isEditing ? updateMutation : createMutation;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSurveyTemplateDTO>({
    defaultValues: {
      title: '',
      description: '',
      status: 'DRAFT',
    },
  });

  // Reset form when dialog opens/closes or editingItem changes
  React.useEffect(() => {
    if (open) {
      reset({
        title: editingItem?.title || '',
        description: editingItem?.description || '',
        status: editingItem?.status || 'DRAFT',
      });
    }
  }, [open, editingItem, reset]);

  const onSubmit = async (data: CreateSurveyTemplateDTO) => {
    try {
      if (isEditing && editingItem) {
        await updateMutation.mutateAsync({
          templateId: editingItem.id,
          data: {
            title: data.title,
            description: data.description,
            status: data.status,
          },
        });
      } else {
        await createMutation.mutateAsync({
          tenantId: tenantId!,
          title: data.title,
          description: data.description,
          status: data.status || 'DRAFT',
        });
      }
      handleClose();
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleClose = () => {
    reset();
    onClose();
    onSuccess?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Survey Template' : 'Create Survey Template'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="title"
              control={control}
              rules={{ required: 'Template title is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Template Title"
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  required
                  fullWidth
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus first field when dialog opens for keyboard UX
                  autoFocus
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Description" multiline rows={3} fullWidth />
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select {...field} label="Status">
                    <MenuItem value="DRAFT">Draft</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="ARCHIVED">Archived</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update Template'
                : 'Create Template'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
